/**
 * HTML Validation and Sanitization Utility
 *
 * This module provides secure HTML validation and sanitization for the Custom HTML block.
 *
 * Security Considerations:
 * - All user-provided HTML is sanitized using DOMPurify to prevent XSS attacks
 * - Script tags and inline event handlers are strictly prohibited
 * - External script imports are blocked
 * - HTML is validated for structure before being allowed
 *
 * @module htmlValidator
 */

import DOMPurify from 'dompurify';

export interface ValidationResult {
  isValid: boolean;
  sanitizedHtml?: string;
  errors: string[];
  warnings: string[];
  byteSize?: number;
}

const MAX_HTML_SIZE = 200 * 1024; // 200KB max

/**
 * Validates and sanitizes user-provided HTML
 *
 * @param html - Raw HTML string from user input
 * @returns ValidationResult containing sanitized HTML and any errors/warnings
 */
export const validateAndSanitizeHtml = (html: string): ValidationResult => {
  const errors: string[] = [];
  const warnings: string[] = [];

  // Check if empty
  if (!html || html.trim().length === 0) {
    errors.push('HTML content cannot be empty');
    return { isValid: false, errors, warnings };
  }

  // Check size
  const byteSize = new Blob([html]).size;
  if (byteSize > MAX_HTML_SIZE) {
    errors.push(`HTML content is too large (${Math.round(byteSize / 1024)}KB). Maximum allowed is 200KB.`);
    return { isValid: false, errors, warnings, byteSize };
  }

  // Check for script tags
  const scriptRegex = /<script[\s\S]*?>[\s\S]*?<\/script>/gi;
  if (scriptRegex.test(html)) {
    errors.push('Script tags are not allowed for security reasons');
  }

  // Check for inline event handlers
  const eventHandlerRegex = /\s+on\w+\s*=\s*["'][^"']*["']/gi;
  if (eventHandlerRegex.test(html)) {
    errors.push('Inline event handlers (onclick, onload, etc.) are not allowed for security reasons');
  }

  // Check for external script imports
  const externalScriptRegex = /<script[^>]+src\s*=\s*["']https?:\/\//gi;
  if (externalScriptRegex.test(html)) {
    errors.push('External script imports are not allowed');
  }

  // Validate HTML structure using DOMParser
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  // Check for parsing errors
  const parserErrors = doc.querySelectorAll('parsererror');
  if (parserErrors.length > 0) {
    errors.push('HTML structure is invalid. Please check for unclosed tags or syntax errors.');
  }

  // If there are errors, return early
  if (errors.length > 0) {
    return { isValid: false, errors, warnings, byteSize };
  }

  // Configure DOMPurify
  const config = {
    ALLOWED_TAGS: [
      'div', 'span', 'p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'ul', 'ol', 'li', 'a', 'img', 'br', 'hr', 'strong', 'em',
      'b', 'i', 'u', 'code', 'pre', 'blockquote', 'table', 'thead',
      'tbody', 'tr', 'th', 'td', 'section', 'article', 'header',
      'footer', 'nav', 'aside', 'main', 'figure', 'figcaption',
      'video', 'audio', 'source', 'canvas', 'svg', 'path', 'circle',
      'rect', 'line', 'polygon', 'polyline', 'ellipse', 'g', 'text',
      'form', 'input', 'button', 'select', 'option', 'textarea', 'label'
    ],
    ALLOWED_ATTR: [
      'class', 'id', 'style', 'href', 'src', 'alt', 'title',
      'width', 'height', 'data-*', 'aria-*', 'role', 'name',
      'type', 'value', 'placeholder', 'required', 'disabled',
      'readonly', 'checked', 'selected', 'for', 'target',
      'rel', 'download', 'controls', 'autoplay', 'loop', 'muted',
      'poster', 'preload', 'viewBox', 'xmlns', 'd', 'fill', 'stroke',
      'stroke-width', 'cx', 'cy', 'r', 'x', 'y', 'x1', 'y1', 'x2', 'y2',
      'points', 'rx', 'ry'
    ],
    FORBID_TAGS: ['script', 'iframe', 'object', 'embed', 'applet', 'base'],
    FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover'],
    ALLOW_DATA_ATTR: true,
    ALLOW_ARIA_ATTR: true,
    SAFE_FOR_TEMPLATES: true,
  };

  // Sanitize HTML
  const sanitizedHtml = DOMPurify.sanitize(html, config);

  // Check if sanitization removed content
  if (sanitizedHtml.length < html.length * 0.5) {
    warnings.push('A significant amount of content was removed during sanitization. Please review the result.');
  }

  // Check for iframe attempts (DOMPurify should block these, but double-check)
  if (html.toLowerCase().includes('iframe') && !sanitizedHtml.toLowerCase().includes('iframe')) {
    warnings.push('iframes were removed from your HTML. If you need embedded content, use supported tags like <video> or <img>.');
  }

  return {
    isValid: true,
    sanitizedHtml,
    errors: [],
    warnings,
    byteSize
  };
};

/**
 * Quick validation without sanitization (for real-time feedback)
 */
export const quickValidate = (html: string): { hasErrors: boolean; errorCount: number } => {
  if (!html || html.trim().length === 0) {
    return { hasErrors: true, errorCount: 1 };
  }

  let errorCount = 0;

  // Check size
  const byteSize = new Blob([html]).size;
  if (byteSize > MAX_HTML_SIZE) {
    errorCount++;
  }

  // Check for prohibited content
  if (/<script/gi.test(html)) errorCount++;
  if (/\s+on\w+\s*=\s*["']/gi.test(html)) errorCount++;

  return { hasErrors: errorCount > 0, errorCount };
};

